import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, Send } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { base44 } from "@/api/base44Client";

export default function ContactModal({ open, onOpenChange, topic }) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await base44.functions.invoke("sendContactMessage", { ...form, topic });
      setSubmitted(true);
      const t = toast({
        title: "Message sent!",
        description: "Thank you for reaching out. We'll get back to you shortly.",
      });
      setTimeout(() => t.dismiss(), 3000);
    } catch {
      setError("Sorry, your message couldn't be sent. Please call or text 360.389.2897.");
    }
    setSending(false);
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-accent" />
            </div>
            <h3 className="text-xl font-bold mb-2">Thank you!</h3>
            <p className="text-muted-foreground text-sm">
              We've received your message and will get back to you shortly.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{topic || "Let's talk"}</DialogTitle>
              <DialogDescription>
                Tell us a little about your situation and we'll reach out fast.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="cm-name">Name</Label>
                <Input
                  id="cm-name"
                  required
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm-email">Email</Label>
                <Input
                  id="cm-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm-phone">Phone (optional)</Label>
                <Input
                  id="cm-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="360.555.1234"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm-message">How can we help?</Label>
                <Textarea
                  id="cm-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Tell us what's going on..."
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={sending} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}